import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Edit3 } from "lucide-react";
import {
  useBookingStatuses,
  useCreateBookingStatus,
  useUpdateBookingStatus,
  useEventCategories,
  type CreateBookingStatusData,
  type UpdateBookingStatusData,
} from "@/hooks/useEventManagement";
import { StatusKeyUpdateDialog } from "./StatusKeyUpdateDialog";

interface BookingStatusFormProps {
  statusId?: string | null;
  onClose: () => void;
}

const colorOptions = [
  { value: "#ef4444", label: "Red", color: "#ef4444" },
  { value: "#f97316", label: "Orange", color: "#f97316" },
  { value: "#eab308", label: "Yellow", color: "#eab308" },
  { value: "#22c55e", label: "Green", color: "#22c55e" },
  { value: "#3b82f6", label: "Blue", color: "#3b82f6" },
  { value: "#8b5cf6", label: "Purple", color: "#8b5cf6" },
  { value: "#6b7280", label: "Gray", color: "#6b7280" },
];

export function BookingStatusForm({ statusId, onClose }: BookingStatusFormProps) {
  const isEditing = !!statusId;
  const [showStatusKeyDialog, setShowStatusKeyDialog] = useState(false);
  const { data: statuses } = useBookingStatuses();
  const { data: categories } = useEventCategories();
  const createStatus = useCreateBookingStatus();
  const updateStatus = useUpdateBookingStatus();

  const form = useForm<CreateBookingStatusData & { is_active: boolean }>({
    defaultValues: {
      status_key: "",
      display_name: "",
      description: "",
      color: "#3b82f6",
      sort_order: 0,
      category: "booking",
      is_active: true,
    },
  });

  const currentStatus = statuses?.find(s => s.id === statusId);

  useEffect(() => {
    if (isEditing && currentStatus) {
      form.reset({
        status_key: currentStatus.status_key,
        display_name: currentStatus.display_name,
        description: currentStatus.description || "",
        color: currentStatus.color,
        sort_order: currentStatus.sort_order,
        category: currentStatus.category,
        is_active: currentStatus.is_active,
      });
    } else if (!isEditing) {
      // Set default sort order for new status
      const maxSort = statuses?.reduce((max, s) => Math.max(max, s.sort_order), 0) || 0;
      form.setValue("sort_order", maxSort + 1);
    }
  }, [isEditing, currentStatus, statuses, form]);

  const onSubmit = async (data: CreateBookingStatusData & { is_active: boolean }) => {
    try {
      if (isEditing && statusId) {
        const updates: UpdateBookingStatusData = {
          display_name: data.display_name,
          description: data.description,
          color: data.color,
          sort_order: data.sort_order,
          category: data.category,
          is_active: data.is_active,
        };
        await updateStatus.mutateAsync({ id: statusId, updates });
      } else {
        const createData: CreateBookingStatusData = {
          status_key: data.status_key,
          display_name: data.display_name,
          description: data.description,
          color: data.color,
          sort_order: data.sort_order,
          category: data.category,
        };
        await createStatus.mutateAsync(createData);
      }
      onClose();
    } catch (error) {
      // Error handling is done in the mutation hooks
    }
  };

  return (
    <>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-2">
        {!isEditing && (
          <div className="space-y-2">
            <Label htmlFor="status_key">Status Key*</Label>
            <Input
              id="status_key"
              placeholder="e.g., pending_review"
              {...form.register("status_key", { required: true })}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Unique identifier for the status (cannot be changed after creation)
            </p>
          </div>
        )}

        {isEditing && currentStatus && (
          <div className="space-y-2">
            <Label>Status Key</Label>
            <div className="flex items-center gap-2">
              <Input
                value={currentStatus.status_key}
                disabled
                className="font-mono bg-muted"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowStatusKeyDialog(true)}
                className="shrink-0"
              >
                <Edit3 className="h-4 w-4 mr-1" />
                Edit
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Click "Edit" to change the status key. This will update all related records.
            </p>
          </div>
        )}

      <div className="space-y-2">
        <Label htmlFor="display_name">Display Name*</Label>
        <Input
          id="display_name"
          placeholder="e.g., Pending Review"
          {...form.register("display_name", { required: true })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          placeholder="Optional description of this status"
          {...form.register("description")}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Category*</Label>
        <Select
          value={form.watch("category")}
          onValueChange={(value) => form.setValue("category", value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories?.map((category) => (
              <SelectItem key={category.category_key} value={category.category_key}>
                <div className="flex flex-col">
                  <span>{category.display_name}</span>
                  {category.description && (
                    <span className="text-xs text-muted-foreground">{category.description}</span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="color">Color*</Label>
        <Select
          value={form.watch("color")}
          onValueChange={(value) => form.setValue("color", value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {colorOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-full border"
                    style={{ backgroundColor: option.color }}
                  />
                  {option.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sort_order">Sort Order</Label>
        <Input
          id="sort_order"
          type="number"
          {...form.register("sort_order", { valueAsNumber: true })}
        />
        <p className="text-xs text-muted-foreground">
          Controls the display order of statuses
        </p>
      </div>

      {isEditing && (
        <div className="flex items-center space-x-2">
          <Switch
            id="is_active"
            checked={form.watch("is_active")}
            onCheckedChange={(checked) => form.setValue("is_active", checked)}
          />
          <Label htmlFor="is_active">Active</Label>
        </div>
      )}

      <div className="flex gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">
          Cancel
        </Button>
        <Button 
          type="submit" 
          className="flex-1"
          disabled={createStatus.isPending || updateStatus.isPending}
        >
          {isEditing ? "Update" : "Create"}
        </Button>
      </div>
    </form>

    {isEditing && currentStatus && (
      <StatusKeyUpdateDialog
        open={showStatusKeyDialog}
        onOpenChange={setShowStatusKeyDialog}
        status={currentStatus}
      />
    )}
    </>
  );
}